/* eslint-disable max-lines */
import mime from 'mime-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import service from 'services';
import selectors from 'state/selectors';
import styled from 'styled-components/macro';
import utils from 'utils';
import { Attachment } from 'models/response';
import { RootState } from 'models';
import Modal from 'components/Modal';
import { FilePosition, FilePositionTypes } from '../../../components/DocumentSearch/index.types';

const Container = styled.div<{ disabled: boolean }>`
  background-color: ${({ disabled, theme }) => disabled && theme.colorPalette.grey.grey1};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const AttachmentBox = styled.div`
  height: 40px;
  min-width: 400px;
  border: 1px solid ${({ theme }) => theme.colorPalette.grey.grey5};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;

  p {
    font-size: ${({ theme }) => theme.fontSizePalette.xxSmall};
  }

  > * {
    margin-left: 10px;
  }
`;

const Flex = styled.div<{ disabled: boolean }>`
  display: ${(props) => (props.disabled ? 'none' : 'flex')};
  align-items: center;
  > * {
    margin: 0 10px;
  }
`;

const ClickableP = styled.p`
  color: ${({ theme }) => theme.colorPalette.turquoise.normal};
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => theme.colorPalette.turquoise.normal};
  cursor: pointer;
`;

interface IFile {
  base64: string;
  extension: string;
  fileName: string;
}

const FileDefault: IFile = {
  base64: '',
  extension: '',
  fileName: '',
};

const Attachments = () => {
  const inputFile = useRef<HTMLInputElement>(null);
  const userCode = useSelector(selectors.user.getIdUser);
  const username = useSelector(selectors.user.getUserName);
  const isArchived = useSelector(selectors.documents.selectIsTemplateArchived);
  const isReadOnly = useSelector(selectors.documents.selectIsTemplateReadOnly);
  const document = useSelector(selectors.documents.selectActiveDocument);
  const { protocol, companyName = '' } = document || {};
  const CAN_VIEW_DISABLED_ATTACHMENT_DATE = useSelector((state: RootState) =>
    selectors.app.isActionByCompanyActive(state, companyName, 'CAN_VIEW_DISABLED_ATTACHMENT_DATE'),
  );
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [file, setFile] = useState<IFile>(FileDefault);
  const [inputKey, setInputKey] = useState<number>(0);
  const t = utils.intl.useTranslator();
  const userPreference = useSelector(selectors.user.getUserPreference);
  const rows = useSelector(selectors.documents.selectTableRows);

  const init = async () => {
    try {
      if (protocol) {
        const filePosition = rows.find((ele) => ele.protocol === protocol)?.position as FilePosition;
        if (isArchived || filePosition === FilePositionTypes.archived) {
          const { data } = await service.getArchivedAttachments({ protocol });
          setAttachments(data);
        } else {
          const { data } = await service.getDocumentAttachments({ protocol });
          setAttachments(data);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const stableInit = useCallback(init, []);

  useEffect(() => {
    stableInit();
  }, [stableInit]);

  const downloadAttachment = async (idDocument: string | number) => {
    if (protocol) {
      let response;
      const filePosition = rows.find((ele) => ele.protocol === protocol)?.position as FilePosition;
      if (isArchived || filePosition === FilePositionTypes.archived) {
        const { data } = await service.getArchivedAttachImage({ protocol, idDocAttach: Number(idDocument) });
        response = data;
      } else {
        const { data } = await service.getDocumentAttach({ idDocAttach: Number(idDocument) });
        response = data;
      }
      const { attach, mimeType, filename } = response;

      if (attach) {
        utils.file.downloadFileFromBase64Source(filename, attach, mimeType);
      }
    }
  };

  const deleteDocumentAttach = async (idDocument: string | number, userCode: number) => {
    try {
      await service.deleteDocumentAttach({ idDocAttach: Number(idDocument), idUserDisable: userCode });
      init();
      utils.app.notify('success', t('file_deleted'));
    } catch (e) {
      utils.app.notify('fail', t('file_non_cancellato'));
    }
  };

  const selectFile = () => {
    inputFile?.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length && username) {
      const file = e.target.files[0];
      const { name, type } = file;
      const extension = name.toLowerCase().endsWith('.msg') ? 'msg' : mime.extension(type);
      const base64 = await utils.file.toBase64(file);
      if (typeof base64 === 'string' && extension) {
        setFile({ fileName: name, extension, base64 });
      }
    }
  };

  const upload = async () => {
    const { base64, extension, fileName } = file;
    try {
      userCode &&
        protocol &&
        (await service.insertDocumentAttach({
          base64Attach: base64,
          extension,
          fileName,
          idUser: userCode,
          protocol,
        }));
      utils.app.notify('success', t('file_uploaded'));
      init();
    } catch (e) {
      utils.app.notify('fail', t('file_non_caricato'));
    }
    setInputKey((prevKey) => {
      return prevKey + 1;
    });
  };

  const openInNewTab = async (idDocument: string | number) => {
    if (!protocol) return;
    const filePosition = rows.find((ele) => ele.protocol === protocol)?.position as FilePosition;
    const serviceCall =
      isArchived || filePosition === FilePositionTypes.archived
        ? service.getArchivedAttachImage({ protocol, idDocAttach: Number(idDocument) })
        : service.getDocumentAttach({ idDocAttach: Number(idDocument) });
    const { data } = await serviceCall;
    const { attach } = data;
    utils.file.openPdfInNewTab(attach);
  };

  const uploadFile = () => {
    file.base64.length > 0 && upload();
  };

  const stableUploadFile = useCallback(uploadFile, [file]);

  useEffect(() => {
    stableUploadFile();
  }, [stableUploadFile]);

  return (
    <>
      <Modal.Header title={t('allegabtn')} subtitle={t('allega-description')} noMargin={attachments.length === 0} />
      <Modal.Content>
        {attachments.map((el, index) => {
          const enabledIndex = attachments.slice(0, index).filter((a) => !a.usernameDisable).length;

          return (
            <Container key={el.idDocAttach} disabled={Boolean(el.usernameDisable)}>
              <AttachmentBox>
                <div>
                  <p>{el.filename}</p>
                </div>

                <Flex disabled={Boolean(el.usernameDisable)}>
                  {el.description && <p>{t(el.description)}</p>}
                  {el.dateIn && <p>{utils.date.convertDate(el.dateIn, userPreference.timeZone)}</p>}
                  <p>{el.fileExtension}</p>
                  {el.fileExtension === 'pdf' && (
                    <ClickableP
                      data-walkthrough={`upload-view-${enabledIndex}`}
                      onClick={() => openInNewTab(el.idDocAttach)}
                    >
                      view
                    </ClickableP>
                  )}
                  <ClickableP
                    data-walkthrough={`upload-download-${enabledIndex}`}
                    onClick={() => downloadAttachment(el.idDocAttach)}
                  >
                    {t('download')}
                  </ClickableP>
                  {!isReadOnly && (
                    <ClickableP
                      data-walkthrough={`upload-delete-${enabledIndex}`}
                      onClick={() => userCode && deleteDocumentAttach(el.idDocAttach, userCode)}
                    >
                      {t('delete')}
                    </ClickableP>
                  )}
                </Flex>
                <Flex disabled={!el.usernameDisable}>
                  <p>{el.usernameDisable}</p>
                  {
                    // not all users con view this date.
                    CAN_VIEW_DISABLED_ATTACHMENT_DATE && el.disableDate ? (
                      <p>{utils.date.convertDate(el.disableDate, userPreference.timeZone)}</p>
                    ) : null
                  }
                </Flex>
              </AttachmentBox>
            </Container>
          );
        })}
        <input
          ref={inputFile}
          style={{ display: 'none' }}
          id="uplaodAttachment"
          type="file"
          onChange={handleFileChange}
          key={inputKey}
        />
      </Modal.Content>
      <Modal.Footer
        dontCloseOnConfirm
        confirmDisabled={isReadOnly}
        confirmText={t('caricaFile')}
        confirmAction={selectFile}
        withCloseButton
      />
    </>
  );
};

export default Attachments;
